import { MarketStatus, RunnerStatusMarketDetails } from '@ps/web-sports-api-types';
import type { MarketId, MarketView, RunnerId } from '@ps/web-sports-api-types';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import useSelectionWatcher from '@swf/sports/hooks/useSelectionWatcher';

import type { SelectionProps } from './Selection';
import { Button } from './components/Button/Button';

export type SelectionMarket = Pick<
  MarketView,
  | 'marketId'
  | 'marketStatus'
  | 'marketType'
  | 'marketTime'
  | 'eventId'
  | 'competitionId'
  | 'eventTypeId'
  | 'legTypes'
  | 'runners'
>;

export interface MultipleSelection {
  market: SelectionMarket;
  runnerId: RunnerId;
}

export interface Props {
  selections: MultipleSelection[];
  odds?: SelectionProps['odds'];
}

export const MultipleSelections: React.FC<Props> = ({ selections, odds }) => {
  const { runnerIds, markets } = selections.reduce<{
    runnerIds: RunnerId[];
    markets: Record<MarketId, SelectionMarket>;
  }>(
    ({ runnerIds, markets }, { runnerId, market }) => {
      runnerIds.push(runnerId);
      markets[market.marketId] = market;
      return { runnerIds, markets };
    },
    { runnerIds: [], markets: {} }
  );

  const selectionRunners = runnerIds
    .map(({ marketId, selectionId }) =>
      markets[marketId].runners.filter((runner) => runner.selectionId === selectionId)
    )
    .flat();

  const isSelected = useSelector((rootState: RootState) =>
    select.betSlip.isEverySelectionInTheBetSlip(rootState, runnerIds)
  );
  const oddsHistory = useSelectionWatcher(odds);
  const dispatch = useDispatch<Dispatch>();

  const onClick = useCallback(() => {
    if (isSelected) {
      dispatch.betSlip.selectionsRemoved(runnerIds);
    } else {
      dispatch.betSlip.selectionsAdded(
        selections.map(({ runnerId, market }) => ({
          selectionId: runnerId.selectionId,
          marketId: runnerId.marketId,
          eventId: market.eventId,
          eventTypeId: market.eventTypeId,
          competitionId: market.competitionId,
          startTime: market.marketTime,
          odds,
          legType:
            market.legTypes.find((legType) => legType === 'SIMPLE_SELECTION') ?? market.legTypes[0],
          runnerStatus: RunnerStatusMarketDetails.ACTIVE,
          marketType: market.marketType
        }))
      );
    }
  }, [isSelected, dispatch.betSlip, runnerIds, selections, odds]);

  const isLocked =
    selectionRunners.some(
      ({ runnerStatus }) => runnerStatus !== RunnerStatusMarketDetails.ACTIVE
    ) || Object.values(markets).some(({ marketStatus }) => marketStatus !== MarketStatus.OPEN);
  const isDisabled = (isLocked || !odds) && !isSelected;

  return (
    <Button
      isDisabled={isDisabled}
      isLocked={isLocked}
      oddsHistory={oddsHistory}
      onClick={onClick}
      selected={isSelected}
      shouldAnimate={!isLocked && !isSelected}
      isStartingPriceMarket={false}
      shouldShowAddedToBetSlip={isSelected}
    />
  );
};
